import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  FlexBox,
  Button,
  Grid,
  Tabs,
  color,
  Tooltip,
  Logo,
  FormDialog
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { actions as authActions, selectors as authSelectors } from 'store/auth';
import { Client, selectors as clientsSelectors } from 'store/clients';
import { selectors as ciSelectors, actions as ciActions } from 'store/clientIntegrations';
import { actions as kiosksActions } from 'store/kiosks';
import { actions as venueActions } from 'store/venues';
import { getMatchPath } from 'utils/urlUtils';
import ActivityLogView from 'views/ActivityLogView';
import ConfigurationView from 'views/ConfigurationView';
import CustomersView from 'views/CustomersView';
import DeliveryLogView from 'views/DeliveryLogView';
import KiosksView from 'views/KiosksView';
import MediaCenterView from 'views/MediaCenterView';
import SettingsView from 'views/SettingsView';
import UsersView from 'views/UsersView';
import PaymentsView from 'views/PaymentsView';
import ProductsView from 'views/ProductsView';
import packageJSON from '../../package.json';
import { useStylesDashboard } from './styled';

// RETURNS THE PATHNAME WITHOUT THE KIOSKID
const pathInit = (path: string) => {
  const match = getMatchPath(path, '/kiosks/:kioskId?');

  // IF THERE IS A MATCH OF PATH AND THAT PATH HAS KIOSKID, SLICE IT OUT
  if (match && match.params.kioskId) return path.slice(0, path.lastIndexOf('/')).trim();
  return path;
};

const DashboardRoutes: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { logout } = useActions({ logout: authActions.logout });
  const { resetVenueAction } = useActions({ resetVenueAction: venueActions.resetAction });
  const { updateShowHiddenKiosks } = useActions({ updateShowHiddenKiosks: kiosksActions.updateShowHiddenKiosks });

  const admin = useSelector(authSelectors.selectAdmin);
  const activeClient = useSelector(clientsSelectors.selectClient);
  const client = useSelector(clientsSelectors.selectClient) as Client;
  const clientIntegrations = useSelector(ciSelectors.selectClientIntegrations);

  const path = useMemo(() => pathInit(location.pathname), [location.pathname]);
  const [to, setTo] = useState<string>(path);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedPopup, setShowUnsavedPopup] = useState(false);
  const [pendingRoute, setPendingRoute] = useState<string | null>(null);

  const classes = useStylesDashboard();

  const globalAdmin = admin?.role === 'GlobalAdmin';
  const readOnlyAdmin = admin?.role === 'ReadOnlyAdmin';
  const activeClientName =
    activeClient?.name && activeClient.name.length > 20
      ? `${activeClient.name.slice(0, 20)}...`
      : (activeClient?.name ?? '');

  useEffect(() => {
    if (!path.includes(to)) setTo(path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const [getClientIntegrations] = useActions([ciActions.getClientIntegrations]);

  useEffect(() => {
    getClientIntegrations();
  }, [activeClient]);

  const hasKisTechnologiesClientIntegration = clientIntegrations?.some(
    (ci) => ci.integration?.code === 'kistechnologies'
  );

  const onRouteChange = (event: React.MouseEvent<HTMLButtonElement>, v: string | number) => {
    if (hasUnsavedChanges && (to === '/payments' || to === '/products' || to === '/settings')) {
      event.preventDefault();
      setPendingRoute(v.toString());
      setShowUnsavedPopup(true);
    } else {
      setTo(v.toString());
    }
  };

  const clearStoreAndLogout = () => {
    resetVenueAction();
    updateShowHiddenKiosks(false);
    logout();
  };

  const openSupportLink = () => {
    window.open(`https://kistechnologies.atlassian.net/servicedesk/customer/portal/4`);
  };

  // IF NO CLIENT GO TO THE ROOT (SIGN IN) PAGE
  if (!client) return <Navigate to="/" />;

  return (
    <FlexBox bg="white" overflow="hidden" raised flexOverride="1" noWrap className={classes.dashboardOuterContainer}>
      <div className={classes.dashboardSideTabs}>
        <FlexBox width={236} direction="column" justify="between" fullHeight borderRight={`1px solid ${color.BORDER}`}>
          <FlexBox className={classes.logoBox}>
            <Logo height={50} />
          </FlexBox>
          <FlexBox direction="column" align="center">
            {activeClient && globalAdmin && (
              <Box padding="4">
                <Typography className={classes.infoText}>
                  Active customer:
                  <br />
                  <Tooltip title={activeClient.name} arrow placement="top" verticalOffset={12}>
                    <span>{activeClientName}</span>
                  </Tooltip>
                </Typography>
              </Box>
            )}
            <Tabs value={to} onChange={onRouteChange}>
              {globalAdmin && (
                <Tabs.Tab iconName="customer" iconViewBox="0 0 24 24" label="Customers" to="/customers" />
              )}
              <Tabs.Tab iconName="credential" label="Users" to="/users" />
              {hasKisTechnologiesClientIntegration && (
                <Tabs.Tab iconName="attachMoney" iconViewBox="-5 0 19 19" label="Payments" to="/payments" />
              )}
              {hasKisTechnologiesClientIntegration && (
                <Tabs.Tab iconName="category" iconViewBox="0 0 24 24" label="Inventory" to="/products" />
              )}
              <Tabs.Tab iconName="kiosks" label="Kiosks" to="/kiosks" />
              <Tabs.Tab iconName="activity_log" iconViewBox="0 0 59 36" label="Activity Log" to="/activityLog" />
              <Tabs.Tab iconName="ticket" iconViewBox="-5 0 59 36" label="Delivery Log" to="/deliveryLog" />
              {!readOnlyAdmin && <Tabs.Tab iconName="media_center" label="Media Center" to="/mediaCenter" />}
              {!readOnlyAdmin && (
                <Tabs.Tab iconViewBox="0 0 24 24" iconName="settings" label="Settings" to="/settings" />
              )}
              {globalAdmin && (
                <Tabs.Tab iconViewBox="0 0 24 24" iconName="settings" label="Configuration" to="/configuration" />
              )}
            </Tabs>
          </FlexBox>
          <FlexBox direction="column" align="center" margin="0 0 5">
            {admin?.name && (
              <Box padding="4 0 0">
                <Typography className={classes.infoText}>
                  Signed in as:
                  <br />
                  {admin.name}
                </Typography>
              </Box>
            )}
            <Box margin="4 0 4">
              <Button customSize="small" width={144} onClick={openSupportLink}>
                Support
              </Button>
            </Box>
            <Button customSize="small" width={144} onClick={clearStoreAndLogout}>
              Sign Out
            </Button>
            <Box margin="4 0 0">
              <Typography className={classes.infoText}>
                Version: {packageJSON.version}
                <br />
                ©KIS Technologies
              </Typography>
            </Box>
          </FlexBox>
        </FlexBox>
      </div>
      <Grid className={classes.dashboardInnerContainer} direction="column">
        <Routes>
          <Route path="/users" element={<UsersView />} />
          <Route path="/activityLog" element={<ActivityLogView />} />
          <Route path="/mediaCenter" element={<MediaCenterView />} />
          <Route path="/deliveryLog" element={<DeliveryLogView />} />
          <Route path="/kiosks" element={<KiosksView />} />
          {globalAdmin && <Route path="/customers" element={<CustomersView />} />}
          {hasKisTechnologiesClientIntegration && (
            <>
              <Route path="/payments" element={<PaymentsView setHasUnsavedChanges={setHasUnsavedChanges} />} />
              <Route path="/products" element={<ProductsView setHasUnsavedChanges={setHasUnsavedChanges} />} />
            </>
          )}
          {!readOnlyAdmin && (
            <Route path="/settings" element={<SettingsView setHasUnsavedChanges={setHasUnsavedChanges} />} />
          )}
          {globalAdmin && <Route path="/configuration" element={<ConfigurationView />} />}
        </Routes>
        {showUnsavedPopup && (
          <FormDialog
            open
            cancelButtonText="Cancel"
            cancelButtonAction={() => {
              setShowUnsavedPopup(false);
            }}
            confirmButtonText="Leave"
            confirmButtonAction={() => {
              if (pendingRoute) {
                setHasUnsavedChanges(false);
                setShowUnsavedPopup(false);
                setTo(pendingRoute);
                navigate(pendingRoute);
                setPendingRoute(null);
              }
            }}
          >
            <Typography fontSize={24} maxWidth="524px" alignItems="center">
              Do you want to leave without saving?
            </Typography>
          </FormDialog>
        )}
      </Grid>
    </FlexBox>
  );
};

export default DashboardRoutes;
